import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Sidebar from './Sidebar.jsx'
import ToastHost from './ToastHost.jsx'
import { showToast } from '../lib/toast.js'
import ComposeDock from './ComposeDock.jsx'
import { DataProvider } from '../lib/data.jsx'
import { useAuth } from '../auth.jsx'
import { installBackGuard } from '../lib/backGuard.js'
import { startUpdateWatch, reloadForUpdate } from '../lib/appVersion.js'
import { getRun, getStream } from '../lib/runHost.js'

// Persistent strip while the owner/admin is viewing the app as another member.
// Deliberately loud (amber, full-width, always on top of the content card) so
// an impersonated session can never be mistaken for your own — one click exits.
function ImpersonationBanner() {
  const { isImpersonating, user, stopImpersonating } = useAuth()
  if (!isImpersonating) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-[#b45309] px-4 py-[7px] text-center text-[12.5px] font-medium text-white">
      <span>
        Viewing as <strong>{user?.name || user?.email}</strong> — your own session is untouched.
      </span>
      <button
        type="button"
        onClick={stopImpersonating}
        className="rounded-[7px] bg-white/20 px-[10px] py-[3px] text-[12px] font-semibold transition-colors hover:bg-white/30"
      >
        Exit
      </button>
    </div>
  )
}

// A NEWER BUILD IS LIVE and this tab could not reload onto it on its own —
// work was in flight, or the reader is mid-page (see lib/appVersion.js for
// when it reloads by itself). One line, one button; it never blocks anything,
// and an operation started from this tab reloads onto the live build first
// whether or not the banner was pressed.
function UpdateBanner({ latest, onDismiss }) {
  if (!latest) return null
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary-tint px-4 py-[6px] text-center text-[12.5px] text-ink [border-bottom:1px_solid_var(--border)]">
      <span>A newer version of Revailo Sales is live.</span>
      <button
        type="button"
        onClick={() => reloadForUpdate(latest)}
        className="rounded-[7px] bg-primary px-[10px] py-[3px] text-[12px] font-semibold text-white transition-colors hover:bg-primary-dark"
      >
        Reload
      </button>
      <button
        type="button"
        onClick={onDismiss}
        className="rounded-[7px] px-[8px] py-[3px] text-[12px] text-dim transition-colors hover:text-ink"
      >
        Later
      </button>
    </div>
  )
}

// Shared app shell. The whole window is a soft grey "canvas"; the sidebar is
// transparent (its nav items float directly on the canvas, Linear-style) and
// the app content lives in a rounded, bordered card that floats on top with a
// small inset gap all around.
//
// The sidebar renders ONCE here — above the app/wizard boundary — so it stays
// mounted when routes swap between the two inner layouts (AppLayout ↔
// WizardLayout). That persistence is what lets the nav's sliding selection
// indicator animate between items instead of snapping.
export default function Shell() {
  // A Cognito session can lapse while the app still shows the user as signed
  // in — the display name lives in localStorage, the bearer token does not.
  // requireIdToken() fires this after it has tried and failed to refresh, so
  // the rep gets one clear prompt instead of every feature failing separately
  // with the server's "Missing bearer token".
  useEffect(() => {
    let shown = false
    function onExpired() {
      if (shown) return
      shown = true
      showToast({
        tone: 'warn',
        title: 'Your session expired',
        body: 'Sign in again to keep working — nothing you have entered is lost.',
        duration: 12000,
      })
      setTimeout(() => { shown = false }, 30000)
    }
    window.addEventListener('auth:expired', onExpired)
    return () => window.removeEventListener('auth:expired', onExpired)
  }, [])

  // STAY ON THE LIVE BUILD. An open tab keeps running whatever it loaded, and
  // the rep's tab once ran the pre-recut Daily Tasks for a day after the
  // recut shipped (see lib/appVersion.js). The watch reloads a background or
  // just-returned-to tab by itself and asks here otherwise; a run in flight
  // or an answer still streaming always holds the reload back.
  const [updateTo, setUpdateTo] = useState('')
  const [updateDismissed, setUpdateDismissed] = useState('')
  useEffect(
    () =>
      startUpdateWatch({
        onStale: ({ latest }) => setUpdateTo(latest),
        isBusy: () => getRun()?.status === 'running' || getStream()?.status === 'streaming',
      }),
    [],
  )

  // Phone layout: the sidebar becomes a slide-over drawer behind a menu button
  // in the Topbar, so the rep can reach every page one-handed in the car. The
  // breakpoint matches Tailwind's md — below it the rail would eat the screen.
  const [navOpen, setNavOpen] = useState(false)
  const location = useLocation()
  const { pathname } = location

  // BACK STAYS PUT ON A BASE PAGE. Cmd+[ / Alt+← sits next to the keys a rep
  // actually uses, and on a working page it used to throw them out onto
  // whatever they visited before — from an Activity evidence table, Deal Flow.
  // On a detail view Back still closes the detail. Mounted HERE so it covers
  // every signed-in page and nothing on the marketing site or the sign-in flow.
  // See lib/backGuard.js for the mechanism and its one real cost.
  const locRef = useRef(location)
  locRef.current = location
  const guardRef = useRef(null)
  useEffect(() => {
    const guard = installBackGuard(() => locRef.current)
    guardRef.current = guard
    return () => {
      guardRef.current = null
      guard.teardown()
    }
  }, [])
  // Re-arm on every navigation: each new base page needs its own spare entry,
  // and every detail view needs the guard to stand down while it is open.
  useEffect(() => {
    guardRef.current?.sync()
  }, [location.key])
  // Navigating closes it: on a phone the drawer covers the page it just opened.
  useEffect(() => setNavOpen(false), [pathname])
  useEffect(() => {
    if (!navOpen) return
    const onKey = (e) => e.key === 'Escape' && setNavOpen(false)
    window.addEventListener('keydown', onKey)
    // The drawer scrolls; the page behind it must not.
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [navOpen])

  return (
    <DataProvider>
      {/* Canvas grey. The sidebar is transparent, so this IS the sidebar colour
          — keep it light enough that the left rail reads as airy, but still a
          step below the content card (#f8fafc) so the card keeps floating. The
          sidebar's hover greys are tuned against this value. */}
      <div className="flex h-screen overflow-hidden bg-[#f1f2f5] dark:bg-[#0a0a0a] [--sidebar-w:264px] max-[860px]:[--sidebar-w:220px] has-[.sidebar-collapsed]:[--sidebar-w:68px]">
        {/* Desktop: the persistent rail. Phone: the same component inside a
            drawer, so there is exactly one nav implementation. */}
        <div className="max-md:hidden">
          <Sidebar />
        </div>
        {navOpen && (
          <>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-40 border-none bg-[rgba(0,0,0,0.45)] md:hidden"
            />
            <div className="fixed inset-y-0 left-0 z-50 w-[280px] max-w-[86vw] overflow-y-auto bg-[#f1f2f5] pb-6 shadow-[0_0_40px_rgba(0,0,0,0.35)] animate-[menuReveal_0.16s_ease] dark:bg-[#0a0a0a] md:hidden">
              <Sidebar />
            </div>
          </>
        )}
        {/* Floating content card. overflow-hidden clips the inner sticky topbar
            and scroll region to the rounded corners. Full-bleed on a phone —
            the inset gap is a desktop affordance and just wastes width. */}
        <div className="min-w-0 flex-1 py-2 pl-2 pr-2 max-md:p-0">
          <div className="flex h-full flex-col overflow-hidden rounded-[12px] bg-panel [border:1px_solid_#dcdce0] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_28px_-16px_rgba(15,23,42,0.22)] dark:[border:1px_solid_#242424] dark:shadow-none max-md:rounded-none max-md:border-0">
            <ImpersonationBanner />
            {updateTo && updateTo !== updateDismissed && (
              <UpdateBanner latest={updateTo} onDismiss={() => setUpdateDismissed(updateTo)} />
            )}
            <Outlet context={{ onOpenNav: () => setNavOpen(true) }} />
          </div>
        </div>
        <ToastHost />
        <ComposeDock />
      </div>
    </DataProvider>
  )
}
